import type { PromotionTypeEnum } from '@empathybroker/tsclient-play/dist/enums/promotion-type.enum'
import type { AbstractDomainConfig } from '@empathybroker/tsclient-play/dist/models/instance/domains/abstract-domain-config'
import type { BrowseDomainConfig } from '@empathybroker/tsclient-play/dist/models/instance/domains/browse-domain-config'
import type { PermissionConfig } from '@empathybroker/tsclient-play/dist/models/instance/domains/permission-config'
import type { SearchDomainConfig } from '@empathybroker/tsclient-play/dist/models/instance/domains/search-domain-config'
import type { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance'
import type {
  ConfigService,
  PermissionsTreeProvider,
  PlayboardConfig,
} from '@empathyco/playboard-core-domain'
import type { InstancesProvider } from './interfaces'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { deepMerge } from '@empathyco/x-deep-merge'
import { interfaces } from 'inversify'
import { TYPES } from '../config/inversify.types'
import { FeatureIdEnum, FilterIdEnum, FilterTypeEnum } from '../enums'
import Context = interfaces.Context

export function getInstancesFromInstanceManagementProvider(context: Context): InstancesProvider {
  const { container } = context
  return async () => {
    const configService = container.get<ConfigService>(TYPES.ConfigService)
    const serviceConfigs = (
      await configService.list({
        filter: {
          service: 'playboard',
        },
      })
    ).results
    return await serviceConfigs.reduce<Promise<Array<Instance>>>(
      async (previousValue, serviceConfig) => {
        const result = await previousValue
        if (serviceConfig.enabled) {
          const instance = await mapInstance(context, serviceConfig as PlayboardConfig)
          result.push(instance)
        }
        return result
      },
      Promise.resolve([]),
    )
  }
}

async function mapInstance(context: Context, serviceConfig: PlayboardConfig): Promise<Instance> {
  return {
    id: serviceConfig.instance,
    ...mapInstanceCustomization(serviceConfig),
    domains: deepMerge(
      {},
      await mapInstancePermissions(context, serviceConfig),
      mapInstanceTools(serviceConfig),
    ),
    ...mapInstanceFilters(serviceConfig),
    ...mapInstanceKpis(serviceConfig),
    ...mapInstanceCatalog(serviceConfig),
    allowMetadataSectionInFacetsAside:
      serviceConfig?.data?.tools?.[DomainEnum.GLOBAL]?.[FeatureIdEnum.CONFIGURATION]
        ?.allowMetadataSectionInFacetsAside ?? false,
    allowOnlyMandatoryFilters:
      serviceConfig?.data?.tools?.[DomainEnum.GLOBAL]?.[FeatureIdEnum.CONFIGURATION]
        ?.allowOnlyMandatoryFilters ?? false,
    allowShowProductInfo:
      serviceConfig?.data?.tools?.[DomainEnum.GLOBAL]?.[FeatureIdEnum.EXPLAIN]
        ?.allowShowProductInfo ?? false,
  }
}

function mapInstanceCustomization({
  data: { customization },
}: PlayboardConfig): Pick<Instance, 'prettyName' | 'logoUrls'> {
  return {
    prettyName: customization?.displayName,
    logoUrls: {
      imagotype: customization?.imageUrls?.logotype ?? customization?.imageUrls?.imagotype,
      isotype: customization?.imageUrls?.isotype,
    },
  }
}

async function mapInstancePermissions(
  { container }: Context,
  serviceConfig: PlayboardConfig,
): Promise<Record<DomainEnum, Pick<AbstractDomainConfig, 'permissions'>>> {
  const permissionsTree = await container.get<PermissionsTreeProvider>(
    TYPES.UserPermissionsTreeProvider,
  )(serviceConfig)
  return {
    [DomainEnum.SEARCH]: {
      permissions: permissionsTree.search as Array<PermissionConfig>,
    },
    [DomainEnum.BROWSE]: {
      permissions: permissionsTree.browse as Array<PermissionConfig>,
    },
    [DomainEnum.GLOBAL]: {
      permissions: permissionsTree.global as Array<PermissionConfig>,
    },
  }
}

function mapInstanceTools({ data: { tools } }: PlayboardConfig): {
  [DomainEnum.SEARCH]: Pick<SearchDomainConfig, 'tools'>
  [DomainEnum.BROWSE]: Pick<BrowseDomainConfig, 'tools'>
} {
  const promotionTool = tools?.[DomainEnum.SEARCH]?.[FeatureIdEnum.PROMOTION]
  const searchConfigurationTool = tools?.[DomainEnum.SEARCH]?.[FeatureIdEnum.CONFIGURATION]
  const browseConfigurationTool = tools?.[DomainEnum.BROWSE]?.[FeatureIdEnum.CONFIGURATION]
  return {
    [DomainEnum.SEARCH]: {
      tools: {
        ...(tools?.[DomainEnum.SEARCH] as unknown as SearchDomainConfig['tools']),
        ...(promotionTool && {
          [FeatureIdEnum.PROMOTION]: {
            types: promotionTool.types.map(type => ({
              id: type as PromotionTypeEnum,
            })),
          },
        }),
        ...(searchConfigurationTool && {
          [FeatureIdEnum.CONFIGURATION]: {
            pivotIds: searchConfigurationTool.pivotIds,
            useSearchConfigurations: searchConfigurationTool.useSearchConfigurations,
          },
        }),
      },
    },
    [DomainEnum.BROWSE]: {
      tools: {
        ...(tools?.[DomainEnum.BROWSE] as unknown as BrowseDomainConfig['tools']),
        ...(browseConfigurationTool && {
          [FeatureIdEnum.CONFIGURATION]: {
            pivotIds: browseConfigurationTool.pivotIds,
            attributes:
              browseConfigurationTool.attributes?.map(attribute => ({
                id: attribute.id,
                prettyName: attribute.displayName,
                isCategory: attribute.isCategory,
              })) ?? [],
            useSearchConfigurations: browseConfigurationTool.useSearchConfigurations,
          },
        }),
      },
    },
  }
}

function mapInstanceFilters({
  data: { filters },
}: PlayboardConfig): Pick<
  Instance,
  'filters' | 'allowMultipleValuesInSearchFilters' | 'additionalFilterOptions'
> {
  const { custom, allowMultipleValuesInSearch, additionalFilterOptions } = filters || {}
  return {
    filters: {
      [FilterIdEnum.DATE]: {
        id: FilterIdEnum.DATE,
        type: FilterTypeEnum.DATE,
      },
      [FilterIdEnum.REALTIME]: {
        id: FilterIdEnum.REALTIME,
        type: FilterTypeEnum.REALTIME,
      },
      ...custom?.reduce(
        (result, filter) => ({
          ...result,
          [filter.id]: {
            id: filter.id,
            type: FilterTypeEnum.REGULAR,
            prettyName: filter.displayName,
            taggingId: filter.taggingId,
            toolingId: filter.taggingId,
            values: filter.values.map(value => ({
              id: value.id,
              prettyName: value.displayName,
            })),
            defaultValueId: filter.defaultValueId,
            onlyAvailableIn: filter.onlyAvailableIn,
            notMandatoryInExplain: filter.notMandatoryInExplain,
          },
        }),
        {},
      ),
    } as any,
    allowMultipleValuesInSearchFilters: allowMultipleValuesInSearch ?? false,
    additionalFilterOptions: { ...additionalFilterOptions },
  }
}

function mapInstanceKpis({ data: { kpis } }: PlayboardConfig): Pick<Instance, 'kpis'> {
  return {
    ...(kpis && {
      kpis: Object.entries(kpis).reduce<Array<string>>((result, [kpi, value]) => {
        if (value) {
          switch (kpi) {
            case 'query':
              result.push('queries')
              break
            case 'noResult':
              result.push('noResults')
              break
            case 'click':
              result.push('clicks')
              break
            case 'session':
              result.push('sessions')
              result.push('queriesPerSession')
              break
            default:
              result.push(kpi)
              break
          }
        }
        return result
      }, []),
    }),
  }
}

function mapInstanceCatalog({ data: { catalog } }: PlayboardConfig): Pick<Instance, 'catalog'> {
  const mappings = catalog?.resultMappings
  return {
    ...(catalog && {
      catalog: {
        fields: catalog.fields.map(field => ({
          ...field,
          id: field.id,
          prettyName: field.displayName,
          rankable: field.rankable,
          restricted: {},
        })),
        resultMappings: mappings
          ? {
              id: mappings.id,
              ...(mappings.customPinnedProductFieldId && {
                customPinnedProductFieldId: mappings.customPinnedProductFieldId,
              }),
              ...(mappings.name && { name: mappings.name }),
              ...(mappings.imageUrl && { imageUrl: mappings.imageUrl }),
            }
          : { id: '' },
        indexToken: '',
      },
    }),
  }
}
